import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Modal } from '@/components/ui/Modal'
import { Spinner } from '@/components/ui/Spinner'
import { Badge, Box, Grid, GridItem, Stack, Text } from '@chakra-ui/react'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useActivityLog } from '../hooks/useActivityLogs'

const JsonValue = ({ value }) => (
  <Box
    bg="#F4F5F7"
    border="1px solid"
    borderColor="#DFE1E6"
    borderRadius="3px"
    whiteSpace="pre-wrap"
    p={3}
    maxH="220px"
    overflowY="auto"
    fontFamily="mono"
    fontSize="12px"
    color="#172B4D"
  >
    {value == null ? '—' : typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
  </Box>
)

const DetailField = ({ label, value, colSpan }) => (
  <GridItem colSpan={colSpan}>
    <Text fontSize="12px" fontWeight="600" color="#5E6C84" mb={1}>
      {label}
    </Text>
    <Text color="#172B4D" wordBreak="break-word">
      {value || '—'}
    </Text>
  </GridItem>
)

const formatAuditDate = (value) => {
  if (!value) return '—'

  try {
    return format(new Date(value), 'dd.MM.yyyy HH:mm:ss')
  } catch {
    return '—'
  }
}

const actionStyles = {
  0: { bg: '#E3FCEF', color: '#006644' },
  1: { bg: '#DEEBFF', color: '#0052CC' },
  2: { bg: '#FFEBE6', color: '#BF2600' },
  3: { bg: '#F4F5F7', color: '#42526E' },
  4: { bg: '#EAE6FF', color: '#403294' },
}

export function ActivityLogDetail({ id, isOpen, onClose }) {
  const { t } = useTranslation()
  const { data, isLoading, error } = useActivityLog(id, isOpen)
  const actionLabels = {
    0: t('audit.create'),
    1: t('audit.update'),
    2: t('audit.delete'),
    3: t('audit.access'),
    4: t('audit.syslog'),
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('audit.details')}${data?.id ? ` #${data.id}` : ''}`}
      size="xl"
      footer={ <Button variant="outline" size="md" onClick={onClose}>{t('common.close')}</Button> }
    >
      {isLoading && <Spinner />}
      {error && <Text color="#DE350B">{t('audit.detailError')}</Text>}
      {data && !isLoading && (
        <Stack gap={4}>
          <Fieldset>
            <Fieldset.Content>
              <Grid templateColumns="repeat(2, minmax(0, 1fr))" gap={4}>
                <GridItem>
                  <Stack gap={3}>
                    <DetailField label={t('audit.date')} value={formatAuditDate(data.created_at)} />
                    <GridItem>
                      <Text fontSize="12px" fontWeight="600" color="#5E6C84" mb={1}>
                        {t('audit.action')}
                      </Text>
                      <Badge
                        bg={(actionStyles[data.action] || actionStyles[3]).bg}
                        color={(actionStyles[data.action] || actionStyles[3]).color}
                        fontSize="11px"
                        fontWeight="600"
                        px={2}
                        py={1}
                        borderRadius="3px"
                      >
                        {actionLabels[data.action] || data.action_display || data.action || '—'}
                      </Badge>
                    </GridItem>
                    <DetailField
                      label={t('audit.adminAction')}
                      value={data.is_admin_action ? t('common.yes') : t('common.no')}
                    />
                  </Stack>
                </GridItem>
                <GridItem colStart={2}>
                  <Stack gap={4}>
                    <DetailField label={t('audit.logType')} value={data.log_type_display || data.log_type} />
                    <DetailField label={t('audit.actor')} value={data.actor_display || data.actor_username} />
                    <DetailField label={t('audit.remoteAddress')} value={data.remote_addr} />
                  </Stack>
                </GridItem>
                <DetailField label={t('audit.object')} value={data.object_repr} colSpan={2} />
                <DetailField label={t('audit.path')} value={data.path} colSpan={2} />
              </Grid>
            </Fieldset.Content>
          </Fieldset>
          <Fieldset>
            <Fieldset.Legend>{t('audit.changes')}</Fieldset.Legend>
            <Fieldset.Content><JsonValue value={data.changes} /></Fieldset.Content>
          </Fieldset>
          <Fieldset>
            <Fieldset.Legend>{t('audit.additionalData')}</Fieldset.Legend>
            <Fieldset.Content><JsonValue value={data.additional_data} /></Fieldset.Content>
          </Fieldset>
          <Fieldset>
            <Fieldset.Legend>{t('audit.userAgent')}</Fieldset.Legend>
            <Fieldset.Content><JsonValue value={data.user_agent} /></Fieldset.Content>
          </Fieldset>
        </Stack>
      )}
    </Modal>
  )
}

export default ActivityLogDetail
